'use client';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import {styled} from '@mui/material/styles';
import Image, {type ImageProps} from 'next/image';
import {usePathname} from 'next/navigation';
import {useState, type ElementType} from 'react';
import {DisclosureChevron, MenuLink, Typography} from '../../primitives';
import {isDevelopmentsActive} from '../activeNav';
import {VIEW_ALL_THRESHOLD} from '../DestinationsMenu';

export interface DestinationsAccordionProps {
  labelKey: string;
  // Country/project names are localized by the backend and rendered verbatim.
  countries: {key: string; name: string; href: string; flag?: ImageProps['src']}[];
  projects: {id: string; name: string; href: string}[];
  countriesViewAllHref: string;
  projectsViewAllHref: string;
  t: (key: string) => string;
}

const AccordionTrigger = styled(ButtonBase, {shouldForwardProp: propName => propName !== 'active'})<{active: boolean}>(
  ({theme, active}) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: 0,
    // White at rest, brand tan when the current page is in the developments section —
    // matching the other mobile nav links.
    color: active ? theme.palette.colors.text.primary.inverse : theme.palette.colors.text.neutral.inverse,
  }),
);

const SubList = styled(Box)<{component?: ElementType}>(({theme}) => ({
  listStyle: 'none',
  margin: theme.spacing(1, 0, 0),
  padding: theme.spacing(0, 0, 0, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const SubGroupLabel = styled(Box)<{component?: ElementType}>(({theme}) => ({
  listStyle: 'none',
  color: theme.palette.colors.text.neutral.inverseMuted,
  marginTop: theme.spacing(1),
}));

function SubGroupText({children}: {children: string}) {
  return (
    <Typography
      as="span"
      family="primary"
      size="body-3"
      weight="medium"
      textTransform="uppercase"
      sx={{color: 'inherit', letterSpacing: '-0.015em'}}
    >
      {children}
    </Typography>
  );
}

// "View All" overflow link — the brand accent, mirroring the desktop mega-menu.
const ViewAllLink = styled(MenuLink)(({theme}) => ({
  color: theme.palette.colors.text.primary.default,
}));

export function DestinationsAccordion({
  labelKey,
  countries,
  projects,
  countriesViewAllHref,
  projectsViewAllHref,
  t,
}: DestinationsAccordionProps) {
  const active = isDevelopmentsActive(usePathname());
  const [expanded, setExpanded] = useState(false);
  return (
    <Box>
      <AccordionTrigger
        type="button"
        active={active}
        aria-expanded={expanded}
        onClick={() => {
          setExpanded(isExpanded => !isExpanded);
        }}
      >
        <Typography as="span" family="primary" size="body-1" weight="medium" sx={{color: 'inherit'}}>
          {t(labelKey)}
        </Typography>
        <DisclosureChevron open={expanded} />
      </AccordionTrigger>
      <Collapse in={expanded}>
        <SubList component="ul">
          <SubGroupLabel component="li">
            <SubGroupText>{t('countries')}</SubGroupText>
          </SubGroupLabel>
          {countries.slice(0, VIEW_ALL_THRESHOLD).map(country => (
            <Box component="li" key={country.key}>
              <MenuLink
                href={country.href}
                weight="regular"
                icon={
                  country.flag && <Image src={country.flag} alt="" width={20} height={14} style={{borderRadius: 2, objectFit: 'cover'}} />
                }
              >
                {country.name}
              </MenuLink>
            </Box>
          ))}
          {countries.length > VIEW_ALL_THRESHOLD && (
            <Box component="li">
              <ViewAllLink href={countriesViewAllHref} weight="regular">
                {t('view_all')}
              </ViewAllLink>
            </Box>
          )}
          {projects.length > 0 && (
            <>
              <SubGroupLabel component="li" sx={{mt: 3}}>
                <SubGroupText>{t('projects')}</SubGroupText>
              </SubGroupLabel>
              {projects.slice(0, VIEW_ALL_THRESHOLD).map(project => (
                <Box component="li" key={project.id}>
                  <MenuLink href={project.href} weight="regular">
                    {project.name}
                  </MenuLink>
                </Box>
              ))}
              {projects.length > VIEW_ALL_THRESHOLD && (
                <Box component="li">
                  <ViewAllLink href={projectsViewAllHref} weight="regular">
                    {t('view_all')}
                  </ViewAllLink>
                </Box>
              )}
            </>
          )}
        </SubList>
      </Collapse>
    </Box>
  );
}

DestinationsAccordion.displayName = 'DestinationsAccordion';
